import axios, { AxiosResponse } from 'axios';
import { saveAs } from 'file-saver';
import qs from 'query-string';

import Config from '../config';
import history from '../../routes/history';

import { getUserToken, clearUserToken } from '../auth';
import { ApplicationError, ClientError, fromApiError } from '@go-shared/errors';
import { toast } from '@go-client/components/UIkit/Toast/Toast';

import { readQs } from '@go-shared/lib/querystring';
import { hasOwnProperty } from '@go-shared/lib/utils';
import { resolveCheckVersionPath, resolveMaintenancePath } from '@go-client/routes';
import urljoin from 'url-join';
import { AppRouter, initClient, InitClientArgs } from '@ts-rest/core';
import { z, ZodType, ZodTypeAny } from 'zod';
import { ValidationError } from '../../../shared/errors';

/**
 * Converts a path into an API url
 * @param path the relative path, such as "/users"
 * @param version the API version, defaults to "v1"
 */
export function url(path: string, version: string = 'v1') {
  let base = Config.API_URL;
  version = version ? `/${version}` : '';
  path = path.startsWith('/') ? path : `/${path}`;

  return `${base}${version}${path}`;
}

export function getApiUrlv2() {
  return urljoin(Config.API_URL, 'v2');
}

export enum RequestMethod {
  Get = 'get',
  Post = 'post',
  Put = 'put',
  Delete = 'delete',
}

export type RequestHeaderKeys = 'Authorization';
export type RequestHeaders = { [K in RequestHeaderKeys]: string } | {};

/**
 * Helper to build the path to the under maintenance mode page with a `redirectFrom` QS parameter
 * Returns the path to the maintenance mode page with the appropriate QS
 */
function buildMaintenanceModePath(): string {
  let redirectFromQs = readQs().redirectFrom;
  if (redirectFromQs && typeof redirectFromQs === 'string') {
    return redirectFromQs;
  }
  // If we don't have a `redirectFrom` QS parameter already, use the current pathname
  let qsString = qs.stringify({
    redirectFrom: history.location.pathname,
  });

  return `${resolveMaintenancePath()}?${qsString}`;
}

/**
 * Helper to build the path to the outdated version page with a `redirectFrom` QS parameter
 * Returns the path to the outdated version page with the appropriate QS
 */
function buildOutdatedVersionPath(): string {
  let qsString = qs.stringify({
    redirectFrom: history.location.pathname,
  });

  return `${resolveCheckVersionPath()}?${qsString}`;
}
/**
 * Make a request call to the API, optionally attaching a token from the session
 * storage.
 * @param method the request type
 * @param path the relative path where to make the call
 * @param sendAuth whether to retrieve and attach a user token to the request (default: true)
 * @param params (optional) the query string params to send with the request
 * @param params (optional) the body params to send with the request
 * @param headers (optional) an object with the request headers
 * @returns a new object
 * @throws ClientError if the credentials could not be found or there was a client error
 * @throws ApplicationError if the request fails
 *
 */
export async function apiRequest<T>(
  method: RequestMethod,
  path: string,
  sendAuth: boolean = true,
  params: any = {},
  data: any = {},
  headers: RequestHeaders = {},
  displayToastOnError = true
): Promise<T> {
  let response = await sendApiRequest<T>(
    method,
    path,
    sendAuth,
    params,
    data,
    headers,
    displayToastOnError
  );
  return response.data;
}

/**
 * Make a GET call to the API, optionally attaching a token from the session
 * storage.
 * @param path the relative path where to make the call
 * @param params (optional) the query string params to send with the request
 * @param sendAuth whether to retrieve and attach a user token to the request (default: true)
 * @returns a new object
 * @throws ClientError if the credentials could not be found or there was a client error
 * @throws ApplicationError if the request fails
 *
 */

export async function apiGet<T>(
  path: string,
  params: object = {},
  sendAuth: boolean = true,
  displayToastOnError = true
): Promise<T> {
  return apiRequest(RequestMethod.Get, path, sendAuth, params, null, {}, displayToastOnError);
}

/**
 * Make a POST call to the API, optionally attaching a token from the session
 * storage.
 * @param path the relative path where to make the call
 * @param data (optional) the body params to send with the request
 * @param params (optional) the querystring params to send with the request
 * @param sendAuth whether to retrieve and attach a user token to the request (default: true)
 * @returns a new object
 * @throws ClientError if the credentials could not be found or there was a client error
 * @throws ApplicationError if the request fails
 *
 */

export async function apiPost<T>(
  path: string,
  data: object = {},
  params: object = {},
  sendAuth: boolean = true,
  displayToastOnError = true
): Promise<T> {
  return apiRequest(RequestMethod.Post, path, sendAuth, params, data, {}, displayToastOnError);
}

/**
 * Make a PUT call to the API, optionally attaching a token from the session
 * storage.
 * @param path the relative path where to make the call
 * @param data (optional) the body params to send with the request
 * @param params (optional) the querystring params to send with the request
 * @param sendAuth whether to retrieve and attach a user token to the request (default: true)
 * @returns a new object
 * @throws ClientError if the credentials could not be found or there was a client error
 * @throws ApplicationError if the request fails
 *
 */

export async function apiPut<T>(
  path: string,
  data: object = {},
  params: object = {},
  sendAuth: boolean = true,
  displayToastOnError = true
): Promise<T> {
  return apiRequest(RequestMethod.Put, path, sendAuth, params, data, {}, displayToastOnError);
}

/**
 * Make a DELETE call to the API, optionally attaching a token from the session
 * storage.
 * @param path the relative path where to make the call
 * @param params (optional) the querystring params to send with the request
 * @param sendAuth whether to retrieve and attach a user token to the request (default: true)
 * @returns a new object
 * @throws ClientError if the credentials could not be found or there was a client error
 * @throws ApplicationError if the request fails
 *
 */

export async function apiDelete<T>(
  path: string,
  params: object = {},
  sendAuth: boolean = true,
  displayToastOnError = true
): Promise<T> {
  return apiRequest(RequestMethod.Delete, path, sendAuth, params, null, {}, displayToastOnError);
}

/**
 * Make a POST call to the API, but show the user a download prompt with the contents of the
 * response. The server should set Content-Disposition: attachment and Content-Type appropriately
 *
 */
export async function apiDownload(
  path: string,
  method: RequestMethod = RequestMethod.Post,
  data: object = {},
  headers: RequestHeaders = {},
  params: object = {},
  sendAuth: boolean = true,
  displayToastOnError = true,
  responseType: 'json' | 'arraybuffer' = 'json'
) {
  let response = await sendApiRequest<any>(
    method,
    path,
    sendAuth,
    params,
    data,
    headers,
    displayToastOnError,
    true,
    responseType
  );

  let contentType = response.headers['content-type'] || 'application/octet-stream';
  let fileName = getFileNameFromResponse(response.headers['content-disposition']);

  let blob = new Blob([response.data], { type: contentType });
  return saveAs(blob, fileName);
}

/**
 * Given a content disposition and a content type, return a filename
 *
 */
const DEFAULT_FILENAME = 'download.data';
export function getFileNameFromResponse(contentDisposition?: string): string {
  if (!contentDisposition) return DEFAULT_FILENAME;
  let match = contentDisposition.match(/attachment;\s*filename="([^"]+)"/);
  if (!match || !match[1]) return DEFAULT_FILENAME;
  return match[1];
}

/**
 * Helper to perform an API request and return the response. In case of errors,
 * @param method the request type
 * @param path the relative path where to make the call
 * @param sendAuth whether to retrieve and attach a user token to the request (default: true)
 * @param params (optional) the query string params to send with the request
 * @param params (optional) the body params to send with the request
 * @param headers (optional) an object with the request headers
 * @returns an AxiosResponse
 * @throws ClientError if the credentials could not be found or there was a client error
 * @throws ApplicationError if the request fails
 */
async function sendApiRequest<T>(
  method: RequestMethod,
  path: string,
  sendAuth: boolean = true,
  params: any = {},
  data: any = {},
  headers: RequestHeaders = {},
  displayToastOnError = true,
  verifyGOVersion = true,
  responseType: 'json' | 'arraybuffer' = 'json'
): Promise<AxiosResponse<T>> {
  let requestHeaders: any = { ...headers };
  if (sendAuth) {
    let userToken = getUserToken();
    if (!userToken) throw new ClientError('Could not find user token');

    requestHeaders.Authorization = `Bearer ${userToken}`;
  }
  if (verifyGOVersion) {
    requestHeaders['x-go-version'] =
      process.env.NODE_ENV === 'test' ? process.env.GO_VERSION : Config.VERSION;
  }

  try {
    let response = await axios({
      method: method.valueOf(),
      url: url(path),
      headers: requestHeaders,
      params,
      data,
      responseType
    });

    return response;
  } catch (e) {
    return handleApiError(e, displayToastOnError);
  }
}

async function handleApiError(e: unknown, displayToastOnError: boolean): Promise<never> {
  let apiError = fromApiError(e);
  if (displayToastOnError) await checkForNetworkIssues(apiError.code, apiError);
  if (apiError.code === 503) {
    // We store the last visited page, or use the one in the query string if it exists
    // We do this since we can still receive 503 in the "under-maintenance" page, and we
    // wouldn't want to make "under-maintenance" the last visited page to avoid a loop

    let maintenanceModeState = axios.isAxiosError(e) ? e.response?.data : undefined;
    history.push(buildMaintenanceModePath(), { maintenanceMode: maintenanceModeState });
  }

  if (apiError.code === 412) {
    // This means the request was made with an old version of the API version cached in
    // the browser. We will update this and reload the website.
    let apiVersion = axios.isAxiosError(e) ? e.response?.data : undefined;
    if (
      typeof apiVersion === 'object' &&
      apiVersion &&
      hasOwnProperty(apiVersion, 'message') &&
      typeof apiVersion.message === 'string'
    ) {
      history.push(buildOutdatedVersionPath());
    }
  }

  if (apiError.code === 401) {
    // FIXME: This is not a great solution because it relies on the format of `/crew/...` for the
    // crew app calls (which need to redirect to the crew app login page)
    let loginPath = history.location.pathname.startsWith('/crew') ? '/crew/login' : '/login';
    clearUserToken();
    history.push(loginPath, { from: history.location.pathname });
  }
  throw apiError;
}
const checkForNetworkIssues = async (statusCode: number, error: ApplicationError) => {
  switch (statusCode) {
    case 503:
      return toast.error(error.message);
    case 400:
      return toast.error(error.message);
    case 401:
      return toast.error('Session expired. Please log in again');
    case 403:
      return toast.error('Insufficient privileges, please contact an administrator');
    case 412:
      return toast.error(`The website is out of date, please perform a hard refresh`);
    default:
      return toast.warning(error.message);
  }
};

interface GoArgs {
  sendAuth: boolean;
  displayToastOnError: boolean;
}

export const initApiClient = <T extends AppRouter>(
  router: T,
  args: InitClientArgs,
  goArgs: GoArgs = { sendAuth: true, displayToastOnError: true }
) => {
  const api = initClient(router, args);

  return new Proxy(api, {
    get(target, prop: string) {
      const method = target[prop]; // this is the endpoint we are calling
      if (method instanceof Function) {
        return async function (...endpointArgs: any[]) {
          const response = await method.apply(target, endpointArgs as any);
          if (response.status === 200) {
            return response.body;
          } else {
            // Instead of instantiating a new Error. We create the 'error' object that
            // fromApiError needs. We get all the info from the response.body
            const e = {
              response: { status: response.body?.code },
              message: response.body?.message,
            };

            await handleApiError(e, goArgs.displayToastOnError);
          }
        };
      }
    },
  });
};

type primitives = 'string' | 'number' | 'boolean' | 'bigint' | 'symbol' | 'undefined' | 'null';
const primitivesToZodSchema: Record<primitives, ZodTypeAny> = {
  string: z.string(),
  number: z.number(),
  boolean: z.boolean(),
  bigint: z.bigint(),
  symbol: z.symbol(),
  undefined: z.undefined(),
  null: z.null(),
};
type PrimitiveMap = {
  string: string;
  number: number;
  boolean: boolean;
  bigint: bigint;
  symbol: symbol;
  undefined: undefined;
  null: null;
};

function getZodSchemaForPrimitive(p: primitives): ZodTypeAny {
  return primitivesToZodSchema[p];
}

export async function safeFetch<T>(
  call: Promise<{ body: unknown }>,
  schema: ZodType<T>
): Promise<T>;

export async function safeFetch<P extends primitives>(
  call: Promise<{ body: unknown }>,
  schema: P
): Promise<PrimitiveMap[P]>;

export async function safeFetch<T>(
  call: Promise<{ body: unknown }>,
  schema: ZodType<T> | primitives
): Promise<T> {
  const res = await call;

  if (typeof schema === 'string') {
    schema = getZodSchemaForPrimitive(schema);
  }

  const result = schema.safeParse(res);

  if (!result.success) {
    throw new ValidationError(`Invalid response format: ${result.error.message}`, []);
  }

  return result.data;
}
